export type FmtHead = {
  chunkId: string;
  chunkSize: number;
  audioFormat: number;
  numChannels: number;
  sampleRate: number;
  byteRate: number;
  blockAlign: number;
  bitsPerSample: number;
  duration: number | null; // In seconds
};

export type Ds64Head = {
  chunkId: string;
  chunkSize: number;
  riffSize: number;
  dataSize: number;
  sampleCountLow: number;
  sampleCountHigh: number;
  tableLength: number;
};

export type WaveHeader = {
  container: string;
  fileSize: number;
  format: string;
};

export class WaveMetaInfo {
  static readonly CONTAINER_RIFF = 'RIFF';
  static readonly CONTAINER_RF64 = 'RF64';

  container: string;
  fileSize: number;
  format: string;
  detectedChunkIDs: string[];
  fmtChunk: FmtHead | null;
  ds64Chunk: Ds64Head | null;

  constructor(header: WaveHeader, fmtChunk: FmtHead | null, ds64Chunk: Ds64Head | null, detectedChunkIDs: string[]) {
    this.container = header.container;
    this.fileSize = header.fileSize;
    this.format = header.format;
    this.fmtChunk = fmtChunk;
    this.ds64Chunk = ds64Chunk;
    this.detectedChunkIDs = detectedChunkIDs;
  }

  isValidContainer(): boolean {
    return this.container === WaveMetaInfo.CONTAINER_RIFF || this.container === WaveMetaInfo.CONTAINER_RF64;
  }
}

export class BrokenWaveFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WaveFormatParserError';
    Object.setPrototypeOf(this, BrokenWaveFileError.prototype);
  }
}

/**
 * Random access reader over the file. Only the requested bytes are read from disk, so chunks
 * (in particular the audio data) are skipped without reading them.
 * - read(): reads exactly `length` bytes (zero-filled past the end of the file);
 *   only use it for small, fixed sizes.
 * - skip(): moves past `length` bytes without reading them;
 *   throws BrokenWaveFileError if that would go past the end of the file.
 */
class ChunkReader {
  private offset = 0;

  constructor(private readonly blob: Blob) {}

  async read(length: number) {
    const bytes = await this.blob.slice(this.offset, this.offset + length).arrayBuffer();
    this.offset += length;

    if (bytes.byteLength === length) {
      return new DataView(bytes);
    }

    // Only at the end of the file: zero-fill the missing bytes.
    const padded = new Uint8Array(length);
    padded.set(new Uint8Array(bytes));
    return new DataView(padded.buffer);
  }

  skip(length: number, chunkId: string) {
    if (this.offset + length > this.blob.size) {
      throw new BrokenWaveFileError(
        `Unexpected end of file while trying to skip ${length} Bytes of chunk "${chunkId}"`,
      );
    }
    this.offset += length;
  }
}

type ChunkHeader = {
  chunkId: string;
  chunkSize: number;
  realChunkSize: number;
};

const readChunkHeader = async (reader: ChunkReader): Promise<ChunkHeader> => {
  const view = await reader.read(8);
  const chunkSize = readInt32(view, 4);
  return { chunkId: readString(view, 0).trim(), chunkSize, realChunkSize: chunkSize + (chunkSize % 2) };
};

const FMT_SIZE = 16;
const DS64_SIZE = 28;

const readFmtChunk = async (reader: ChunkReader, header: ChunkHeader): Promise<FmtHead> => {
  if (header.chunkSize < FMT_SIZE) {
    throw new BrokenWaveFileError(`Chunk "fmt" has invalid size ${header.chunkSize}`);
  }

  const view = await reader.read(FMT_SIZE);
  reader.skip(header.realChunkSize - FMT_SIZE, header.chunkId);

  return {
    chunkId: header.chunkId,
    chunkSize: header.realChunkSize,
    audioFormat: readInt16(view, 0),
    numChannels: readInt16(view, 2),
    sampleRate: readInt32(view, 4),
    byteRate: readInt32(view, 8),
    blockAlign: readInt16(view, 12),
    bitsPerSample: readInt16(view, 14),
    duration: null,
  };
};

const readDs64Chunk = async (reader: ChunkReader, header: ChunkHeader): Promise<Ds64Head> => {
  if (header.chunkSize < DS64_SIZE) {
    throw new BrokenWaveFileError(`Chunk "ds64" has invalid size ${header.chunkSize}`);
  }

  const view = await reader.read(DS64_SIZE);
  reader.skip(header.realChunkSize - DS64_SIZE, header.chunkId);

  return {
    chunkId: header.chunkId,
    chunkSize: header.realChunkSize,
    riffSize: readInt64(view, 0),
    dataSize: readInt64(view, 8),
    sampleCountLow: readInt32(view, 16),
    sampleCountHigh: readInt32(view, 20),
    tableLength: readInt32(view, 24),
  };
};

const skipDataChunk = (reader: ChunkReader, header: ChunkHeader, ds64Chunk: Ds64Head | null) => {
  // RF64 stores the real data size in ds64, the chunk header only holds a placeholder.
  const dataSize = ds64Chunk?.dataSize || header.realChunkSize;
  // Only the size is needed, the audio itself is never read.
  // ds64 holds the unpadded size, so the pad byte of an odd-sized chunk has to be added here.
  reader.skip(dataSize + (dataSize % 2), header.chunkId);
  return dataSize;
};

export const parseWaveMetaInfo = async (file: Blob) => {
  const reader = new ChunkReader(file);
  const headerView = await reader.read(12);
  const waveHeader: WaveHeader = {
    container: readString(headerView, 0),
    fileSize: readInt32(headerView, 4),
    format: readString(headerView, 8),
  };

  const detectedChunkIDs: string[] = [];
  let fmtChunk: FmtHead | null = null;
  let ds64Chunk: Ds64Head | null = null;
  let dataSize: number | null = null;

  if (waveHeader.container !== WaveMetaInfo.CONTAINER_RIFF && waveHeader.container !== WaveMetaInfo.CONTAINER_RF64) {
    return new WaveMetaInfo(waveHeader, null, null, detectedChunkIDs);
  }

  for (let header = await readChunkHeader(reader); header.realChunkSize > 0; header = await readChunkHeader(reader)) {
    detectedChunkIDs.push(header.chunkId);

    if (header.chunkId === 'fmt') {
      fmtChunk = await readFmtChunk(reader, header);
    } else if (header.chunkId === 'ds64') {
      ds64Chunk = await readDs64Chunk(reader, header);
    } else if (header.chunkId === 'data') {
      // A valid WAVE file has exactly one data chunk; a second one makes the duration ambiguous.
      if (dataSize !== null) {
        throw new BrokenWaveFileError('Multiple data chunks found in file');
      }

      dataSize = skipDataChunk(reader, header, ds64Chunk);
    } else {
      reader.skip(header.realChunkSize, header.chunkId);
    }
  }

  if (fmtChunk) {
    fmtChunk.duration = dataSize ? dataSize / fmtChunk.byteRate : null;
  }

  return new WaveMetaInfo(waveHeader, fmtChunk, ds64Chunk, detectedChunkIDs);
};

const readString = (dv: DataView, offset: number) => new TextDecoder().decode(new Uint8Array(dv.buffer, offset, 4));

// WAVE stores all numbers little-endian.
const readInt16 = (dv: DataView, offset: number) => dv.getUint16(offset, true);
const readInt32 = (dv: DataView, offset: number) => dv.getUint32(offset, true);
const readInt64 = (dv: DataView, offset: number) => Number(dv.getBigUint64(offset, true));
